import { Anchor, Box, Button, Divider, Group, List, Text } from "@mantine/core";
import { Paths, PathValue } from "@mctypes/global.types";
import * as R from "ramda";
import React, { useEffect } from "react";
import { useDebounce } from "usehooks-ts";
import { ZodError } from "zod";
import { setShowSuccessMessageThunk } from "../../../../../global/globalSlice";
import { useAppDispatch } from "../../../../../hooks";
import { ISJArapport, ZSJArapportSchema } from "@mcapps/shared/reportSchemas/sjaSchema";
import { openapi } from "../../../../../../openapi";
import SJADeltagere from "./SJADeltagere";
import SJAGenerellInfo from "./SJAGenerellInfo";
import SJAOptions from "./SJAOptions";

const SJARapport: React.FC<{
  data: ISJArapport;
  createdAt?: string;
  onUpdateEditData: (data: ISJArapport) => void;
  projectId?: string;
}> = ({ data, createdAt, onUpdateEditData, projectId }) => {
  const [editData, setEditData] = React.useState(R.clone(data));
  const debouncedValue = useDebounce<ISJArapport>(editData, 1000);

  // const dropdownValues = Object.values(data.options ?? {}).map((option) => option.id); // I tilfelle det skal legges til flere fag i denne løsningen

  useEffect(() => {
    onUpdateEditData(data);
  }, []);

  const dispatch = useAppDispatch();

  useEffect(() => {
    validateAndOrThrowZodError();
  }, [debouncedValue]);

  const validateAndOrThrowZodError = () => {
    const result = ZSJArapportSchema.safeParse(editData);
    if (result.success === false) {
    } else {
      onUpdateEditData(editData);
    }
  };

  React.useEffect(() => {
    setEditData(null);
    setTimeout(() => {
      setEditData(R.clone(data));
    }, 50);
  }, [createdAt]);

  const fetchProjectDataAndSetData = async () => {
    try {
      const mcProject = await dispatch(openapi.endpoints.getMcProject.initiate({ projectId })).unwrap();
      if (mcProject == null) {
        return;
      }
      dispatch(setShowSuccessMessageThunk("Hentet prosjektdata"));
      let updatedData = R.clone(editData);
      updatedData.generellInfo.oppdragsNavn = mcProject.name;
      setEditData({
        ...editData,
        ...updatedData,
      });
    } catch (error) {}
  };

  const updateData = <P extends Paths<ISJArapport, 4>>(path: P, value: PathValue<ISJArapport, P>) => {
    setEditData((prevState) => {
      const updatedData = R.assocPath(path.split("."), value, prevState);
      return updatedData;
    });
  };

  return (
    <Box h="100%" w="100%">
      {editData !== null && (
        <>
          <Button onClick={() => fetchProjectDataAndSetData()}>Hent prosjektnavn</Button>
          <Group align="flex-start" mt="sm" wrap="nowrap" p="md">
            <SJAGenerellInfo editData={editData} updateData={updateData} />
            <SJADeltagere editData={editData} updateData={updateData} />
          </Group>
          <Box p="md">
            <Divider
              mb="sm"
              label={
                <Text fz="lg" fw={600}>
                  Huskeliste
                </Text>
              }
              labelPosition="left"
            />
            <Text fw={600} mb="sm">
              Før vi skal ut av kontoret (befaring, test etc.) skal vi utarbeide en skriftlig SJA. Her er stegene i
              prioritert rekkefølge for dette:
            </Text>
            <List withPadding type="ordered" spacing="xs" fz="sm" fw={600}>
              <List.Item>
                Snakk med kunden på forhånd - hvilke HMS/SHA instrukser gjelder, og hvem har ansvar.
              </List.Item>
              <List.Item>Les vår feltinstruks.</List.Item>
              <List.Item>
                SJA fylles ut i MCApps (
                <Anchor
                  href="https://multiconsultas.sharepoint.com/sites/KNVE-Wiki/SitePages/SJA.aspx"
                  target="_blank"
                  rel="noopener noreferrer"
                  fw={600}
                >
                  Se veiledning på wiki
                </Anchor>
                ).
              </List.Item>
              <List.Item>Oversend SJA til kunden.</List.Item>
              <List.Item>Ta med utfylt SJA på plassen – revider hvis noe er endret.</List.Item>
              <List.Item>Alltid ta en gjennomgang av SJA på plassen før man starter med befaring/test etc.</List.Item>
              <List.Item>Hvis det er endringer – Ta TO!</List.Item>
            </List>
            <Text mt="sm" fw={600}>
              For mer informasjon, se{" "}
              <Anchor
                href="https://livelink.multiconsult.no/ll/livelink.exe?func=ll&objaction=overview&objid=9084061"
                target="_blank"
                rel="noopener noreferrer"
                fw={600}
              >
                SJA-retningslinjer
              </Anchor>
              .
            </Text>
          </Box>
          <SJAOptions editData={editData} updateData={updateData} />
        </>
      )}
    </Box>
  );
};

export default SJARapport;
